import { useEffect, useRef, useState } from 'react';
import '../styles/FloraMap.css';

const API_BASE_URL = 'https://your-garden-guru-backend.vercel.app/api';

// Helper to load scripts
const loadScript = (url, id) => {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.src = url;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

// Helper to load CSS
const loadCSS = (url, id) => {
  return new Promise((resolve) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }

    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = url;
    link.onload = resolve;
    document.head.appendChild(link);
  });
};

const FloraMap = () => {
  const mapContainerRef = useRef(null);
  const leafletMapRef = useRef(null);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (leafletMapRef.current) return;

    const initMap = async () => {
      try {
        // Load CSS first
        await Promise.all([
          loadCSS('https://unpkg.com/leaflet@1.9.4/dist/leaflet.css', 'leaflet-css'),
          loadCSS('https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css', 'markercluster-css'),
          loadCSS('https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css', 'markercluster-default-css')
        ]);

        // Load Leaflet first
        await loadScript('https://unpkg.com/leaflet@1.9.4/dist/leaflet.js', 'leaflet-js');

        // Ensure Leaflet is loaded
        if (!window.L) {
          throw new Error("Leaflet failed to load");
        }

        // Then load MarkerCluster
        await loadScript('https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js', 'markercluster-js');

        // Add short delay to ensure plugin is initialized
        await new Promise(resolve => setTimeout(resolve, 200));

        // Check if MarkerCluster is properly loaded
        if (!window.L.MarkerClusterGroup) {
          console.warn("MarkerClusterGroup not available, attempting to use alternative approach");
          // Try alternative path
          await loadScript('https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster-src.js', 'markercluster-src-js');
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        // Now create the map
        const map = L.map(mapContainerRef.current).setView([-25.5, 137], 4);
        leafletMapRef.current = map;

        L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
          attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
          subdomains: 'abcd',
          maxZoom: 18
        }).addTo(map);

        // Check if MarkerClusterGroup is available
        const useFallback = !L.MarkerClusterGroup;

        // Create a cluster group or fallback to basic layer group
        const clusterGroup = useFallback ?
          L.layerGroup() :
          L.markerClusterGroup();

        if (useFallback) {
          console.warn("Using fallback layer group instead of marker clusters");
        }

        fetch(`${API_BASE_URL}/database/tables/flora/data`)
          .then(res => res.json())
          .then(floraData => {
            floraData.sampleData.forEach(f => {
              if (f.latitude === null || f.longitude === null) return;

              const isCritical = f.status === "Critically Endangered";
              const statusClass = isCritical ? "status-critical" : "status-endangered";
              const tagClass = isCritical ? "tag-glow-critical" : "tag-glow-endangered";
              const tagLabel = isCritical ? "🛑 Priority Species" : "⚠️ Threatened Species";

              const popup = `
                <div style="position: relative; max-width: 280px; background: var(--card-bg); 
                border-radius: 12px; box-shadow: 0 4px 10px var(--shadow); padding: 12px; font-size: 13px; 
                color: var(--text-color); overflow: hidden;">

                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <div class="status-tag ${tagClass}">${tagLabel}</div>
                  <button onclick="document.body.setAttribute('data-theme', 
                  document.body.getAttribute('data-theme') === 'dark' ? 'light' : 'dark')" 
                  class="theme-toggle-btn">🌗 Toggle Theme</button>
                </div>

                <a href="${f.website_url}" target="_blank" style="display: block; margin-top: 6px;">
                  <img src="${f.picture_url}" alt="${f.common_name}" style="width: 100%; height: 180px; 
                  object-fit: cover; border-radius: 10px; box-shadow: 0 2px 6px var(--img-shadow);" />
                </a>

                <div style="text-align: center; font-size: 11px; color: var(--hint-color); 
                font-style: italic; margin: 6px 0;">Click image to learn more</div>

                <div style="font-weight: bold; font-size: 14px; margin-bottom: 6px;">
                  🌿 ${f.common_name || f.genus_species}
                </div>

                <div style="margin-bottom: 4px;"><b>Scientific Name:</b> <i>${f.genus_species}</i></div>

                <div class="status-badge ${statusClass}">
                  ${f.status.toUpperCase()} (${f.effective_date})
                </div>

                <div style="margin-top: 4px;"><b>Distribution:</b> ${f.distribution}</div>
                </div>
              `;

              const icon = L.divIcon({
                className: 'custom-plant-icon',
                html: `<div>🌱</div>`,
                iconSize: [30, 30],
                iconAnchor: [15, 15],
                popupAnchor: [0, -15]
              });

              const marker = L.marker(
                [parseFloat(f.latitude), parseFloat(f.longitude)],
                { icon }
              ).bindPopup(popup);

              clusterGroup.addLayer(marker);
            });

            map.addLayer(clusterGroup);
          })
          .catch(error => {
            console.error("Error fetching flora data:", error);
          });

        // Set default theme
        document.body.setAttribute('data-theme', 'light');
      } catch (error) {
        console.error("Error initializing map:", error);
      }
    };

    initMap();

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, []);

  return (<div id="map" ref={mapContainerRef}>
    <button
      id="resetViewBtn"
      className="pixel-button reset-btn"
      onClick={() => {
        if (leafletMapRef.current) {
          leafletMapRef.current.closePopup();
          leafletMapRef.current.flyTo([-25.5, 137], 4, {
            animate: true,
            duration: 1.2
          });
        }
      }}
    >
      Map reset to full view
    </button>

    {/* Help Panel (shown by default) */}
    {showHelp && (
      <div className="help-overlay">
        <div className="help-content">
          <button
            className="close-help-btn"
            title="Close"
            onClick={() => setShowHelp(false)}
          >
            ✖
          </button>
          <h3><b> How to use</b></h3>
          <ul>
            <li>1. Zoom or drag to explore</li>
            <li>2. Click a cluster to zoom into the area</li>
            <li>3. Click a plant icon to view its details</li>
            <li>4. Click plant image to navigate to its full info page</li>
            <br></br>
            <li><b>Additional tip:</b></li>
            <li>Press toggle theme on a plant card to change to dark mode</li>
          </ul>
        </div>
      </div>
    )}

    {/* Help Button */}
    {!showHelp && (
      <button
        className="pixel-button help-btn"
        onClick={() => setShowHelp(true)}
      >
        ℹ️ Help
      </button>
    )}
  </div>
  )
};

export default FloraMap;